import React, { useState, useCallback, useMemo } from 'react';
import { Helmet } from 'react-helmet-async'; // Import Helmet
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FaSearch, FaChevronDown, FaChevronRight, FaPlus, FaCheck, FaEnvelope, FaExternalLinkAlt } from 'react-icons/fa';
import { IconWrapper } from '../components/icons/IconWrapper';
import { useQuote } from '../contexts/QuoteContext';
import Notification from '../components/ui/Notification';

// Updated categories with subcategories from Electrocables USA website
const CATEGORIES = [
  { id: 'all', name: 'All Products' },
  {
    id: 'aluminum-building-wire',
    name: 'Aluminum Building Wire',
    subcategories: [
      { id: 'xhhw', name: 'XHHW' },
      { id: 'ser', name: 'SER' },
      { id: 'seu', name: 'SEU' },
      { id: 'mc-cable', name: 'MC Cable' },
      { id: 'use-rhh-rhw-2', name: 'RHH RHW USE-2' },
      { id: 'mobile-home-feeder', name: 'Mobile Home Feeder' },
      { id: 'pv-wire', name: '2KV PV Wire' }
    ]
  },
  {
    id: '600-volt-ud',
    name: '600 Volt UD',
    subcategories: [
      { id: 'ud-duplex', name: 'Duplex' },
      { id: 'ud-triplex', name: 'Triplex' },
      { id: 'ud-quadruplex', name: 'Quadruplex' },
      { id: 'ud-tri-rated', name: 'Tri-Rated (8000 Series)' }
    ]
  },
  {
    id: 'service-drop',
    name: 'Service Drop',
    subcategories: [
      { id: 'sd-duplex', name: 'Duplex' },
      { id: 'sd-triplex', name: 'Triplex' },
      { id: 'sd-quadruplex', name: 'Quadruplex' }
    ]
  },
  {
    id: 'bare-aluminum',
    name: 'Bare Aluminum',
    subcategories: [
      { id: 'aaac', name: 'AAAC' },
      { id: 'acsr', name: 'ACSR' },
      { id: 'aac', name: 'AAC' }
    ]
  },
  {
    id: 'medium-voltage',
    name: 'Medium Voltage Cables',
    subcategories: [
      { id: 'mv-90', name: 'MV-90' },
      { id: 'mv-105', name: 'MV-105' }
    ]
  },
  {
    id: 'portable-cord',
    name: 'Portable Cord',
    subcategories: [
      { id: 'soow-ul', name: 'SOOW UL' },
      { id: 'soow-non-ul', name: 'SOOW Non-UL' },
      { id: 'sjoow', name: 'SJOOW' },
      { id: 'welding-cable', name: 'Welding Cable' }
    ]
  }
];


// Extended product data with subcategories
const PRODUCTS = [
  // Aluminum Building Wire
  {
    id: 1,
    name: 'XHHW-2 Aluminum Conductor',
    category: 'aluminum-building-wire',
    subcategory: 'xhhw',
    image: '/images/aluminum-wire.jpg',
    description: 'Cross-linked polyethylene (XLPE) insulated aluminum building wire for commercial and industrial power distribution.',
    specifications: ['600V Rated', 'Sunlight Resistant', 'CT Rated (1/0 & larger)', 'AA-8000 Aluminum Alloy'],
    specsUrl: 'https://drive.google.com/file/d/1Gifm9C11HITjcOXWYYnEel3tyRwYWJVn/view?usp=sharing',
  },
  {
    id: 2,
    name: 'SER Service Entrance Cable',
    category: 'aluminum-building-wire',
    subcategory: 'ser',
    image: '/images/aluminum-wire.jpg',
    description: 'Service Entrance Cable - Round (SER) for use as a panel feeder in multiple unit dwellings and for branch circuits.',
    specifications: ['600V Rated', 'XHHW-2 Conductors', 'Reinforcement Tape', 'Grey PVC Jacket'],
    specsUrl: 'https://drive.google.com/file/d/1gdHVuRbgnQpRqoTHbywzE3OS86BWYuL0/view?usp=sharing',
  },
  {
    id: 3,
    name: 'SEU Service Entrance Cable',
    category: 'aluminum-building-wire',
    subcategory: 'seu',
    image: '/images/aluminum-wire.jpg',
    description: 'Service Entrance Cable - Unarmored (SEU) used as a service drop from the utility transformer to the meter base.',
    specifications: ['600V Rated', 'XHHW-2 Conductors', 'Concentric Neutral', 'Grey PVC Jacket'],
    specsUrl: 'https://drive.google.com/file/d/1yZR9Ty_uK2EzO1j9sM3QZNzrb087fdt_/view?usp=sharing',
  },
  {
    id: 4,
    name: 'Aluminum MC Cable',
    category: 'aluminum-building-wire',
    subcategory: 'mc-cable',
    image: '/images/aluminum-wire.jpg',
    description: 'Aluminum Metal Clad (MC) cable for branch, feeder and service power distribution in commercial and industrial buildings.',
    specifications: ['600V Rated', 'XHHW-2 Conductors', 'Interlocked Aluminum Armor', 'Polypropylene Wrap'],
    specsUrl: 'https://drive.google.com/file/d/1l5cs4Q5w1_imBnyDMAgGQNVeZQRcw8aP/view?usp=sharing',
  },
  {
    id: 5,
    name: 'RHH RHW-2 USE-2 Aluminum Conductor',
    category: 'aluminum-building-wire',
    subcategory: 'use-rhh-rhw-2',
    image: '/images/aluminum-wire.jpg',
    description: 'High heat and moisture resistant aluminum conductor for underground service entrance and secondary distribution.',
    specifications: ['600V Rated', 'XLPE Insulation', 'Direct Burial', 'Gas & Oil Resistant'],
    specsUrl: 'https://drive.google.com/file/d/1mFkQ5TzvWGnPGWHee3QNCINmrdhmOXgl/view?usp=sharing',
  },
  {
    id: 6,
    name: 'Mobile Home Feeder Cable',
    category: 'aluminum-building-wire',
    subcategory: 'mobile-home-feeder',
    image: '/images/aluminum-wire.jpg',
    description: 'Aluminum conductor cable specifically designed for underground service to mobile homes.',
    specifications: ['600V Rated', 'RHH/RHW-2/USE-2 Conductors', 'Four Conductor Construction', 'Quadruplexed'],
    specsUrl: 'https://drive.google.com/file/d/1diUPEGO1TY5FAzMglFRmVmbgIaCsIbqW/view?usp=sharing',
  },
  {
    id: 7,
    name: '2KV PV Wire Aluminum Conductor',
    category: 'aluminum-building-wire',
    subcategory: 'pv-wire',
    image: '/images/aluminum-wire.jpg',
    description: 'Photovoltaic (PV) wire for interconnection of solar power systems.',
    specifications: ['2000V Rated', 'XLPE Insulation', 'VW-1 Flame Rating', 'Sunlight Resistant'],
    specsUrl: 'https://drive.google.com/file/d/1poCmjMsOfCiMmy7wjeOoIqUUPy5qeDEK/view?usp=sharing',
  },

  // 600 Volt UD
  {
    id: 8,
    name: '600V UD Duplex Aluminum',
    category: '600-volt-ud',
    subcategory: 'ud-duplex',
    image: '/images/ud-cable.jpg',
    description: 'Duplex underground distribution cable for secondary power distribution.',
    specifications: ['600V Rated', '1350-H19 Aluminum', 'XLPE Insulation', 'Phase ID Strips'],
    specsUrl: 'https://drive.google.com/file/d/1U5v5a2yX_txWWO2XaxsTBMWryl72KSh-/view?usp=sharing',
  },
  {
    id: 9,
    name: '600V UD Triplex Aluminum',
    category: '600-volt-ud',
    subcategory: 'ud-triplex',
    image: '/images/ud-cable.jpg',
    description: 'Triplex underground distribution cable for residential and commercial applications.',
    specifications: ['600V Rated', '1350-H19 Aluminum', 'XLPE Insulation', 'Sequential Footage Marks'],
    specsUrl: 'https://drive.google.com/file/d/1k-R7_OyxVCjK6MQIEwXgRKmNXI8SsYgz/view?usp=sharing',
  },
  {
    id: 10,
    name: '600V UD Quadruplex Aluminum',
    category: '600-volt-ud',
    subcategory: 'ud-quadruplex',
    image: '/images/ud-cable.jpg',
    description: 'Quadruplex underground distribution cable for three-phase secondary distribution.',
    specifications: ['600V Rated', '1350-H19 Aluminum', 'XLPE Insulation', 'Direct Burial'],
    specsUrl: 'https://drive.google.com/file/d/1U5v5a2yX_txWWO2XaxsTBMWryl72KSh-/view?usp=sharing',
  },
  {
    id: 11,
    name: '600V Secondary UD Tri-Rated 8000 Series',
    category: '600-volt-ud',
    subcategory: 'ud-tri-rated',
    image: '/images/ud-cable.jpg',
    description: 'Triple rated underground distribution cable using 8000 series aluminum alloy.',
    specifications: ['600V Rated', '8000 Series Alloy', 'RHH/RHW-2/USE-2 Rated', 'High Flexibility'],
    specsUrl: 'https://drive.google.com/file/d/1U5v5a2yX_txWWO2XaxsTBMWryl72KSh-/view?usp=sharing',
  },

  // Service Drop
  {
    id: 12,
    name: 'Service Drop Duplex Aluminum',
    category: 'service-drop',
    subcategory: 'sd-duplex',
    image: '/images/service-drop.jpg',
    description: 'Overhead service drop cable for single-phase residential applications.',
    specifications: ['600V Rated', 'AAC/ACSR Neutral', 'XLPE Insulation', 'Weather Resistant'],
    specsUrl: 'https://drive.google.com/file/d/148r2NcQS0m7oTfhXBCZWA3znXt1QVVuS/view?usp=sharing',
  },
  {
    id: 13,
    name: 'Service Drop Triplex Aluminum',
    category: 'service-drop',
    subcategory: 'sd-triplex',
    image: '/images/service-drop.jpg',
    description: 'Overhead service drop cable for powering consumer weatherheads.',
    specifications: ['600V Rated', 'AAC/ACSR Neutral', 'XLPE Insulation', 'High Conductivity'],
    specsUrl: 'https://drive.google.com/file/d/148r2NcQS0m7oTfhXBCZWA3znXt1QVVuS/view?usp=sharing',
  },
  {
    id: 14,
    name: 'Service Drop Quadruplex Aluminum',
    category: 'service-drop',
    subcategory: 'sd-quadruplex',
    image: '/images/service-drop.jpg',
    description: 'Overhead service drop cable for supplying three-phase power.',
    specifications: ['600V Rated', 'AAC/ACSR Neutral', 'XLPE Insulation', 'Reliable Performance'],
    specsUrl: 'https://drive.google.com/file/d/148r2NcQS0m7oTfhXBCZWA3znXt1QVVuS/view?usp=sharing',
  },

  // Bare Aluminum
  {
    id: 15,
    name: 'AAAC All Aluminum Alloy Conductor',
    category: 'bare-aluminum',
    subcategory: 'aaac',
    image: '/images/bare-aluminum.jpg',
    description: 'Bare overhead transmission conductor using high-strength aluminum alloy.',
    specifications: ['Excellent Strength', 'Corrosion Resistant', 'Primary/Secondary Distribution', 'ASTM Standards'],
    specsUrl: 'https://drive.google.com/file/d/1kb0JjtpsptPCjZONmMRn7B7wScPFVJIb/view?usp=sharing',
  },
  {
    id: 16,
    name: 'ACSR Aluminum Conductor Steel Reinforced',
    category: 'bare-aluminum',
    subcategory: 'acsr',
    image: '/images/bare-aluminum.jpg',
    description: 'High-strength bare conductor for long-span overhead transmission lines.',
    specifications: ['Steel Core Strength', 'High Conducitivity', 'Primary Distribution', 'Temperature Stable'],
    specsUrl: 'https://drive.google.com/file/d/1kNcajd6W_rv_fl-9wvpi5eWnT1jRDgNb/view?usp=sharing',
  },
  {
    id: 17,
    name: 'AAC All Aluminum Conductor',
    category: 'bare-aluminum',
    subcategory: 'aac',
    image: '/images/bare-aluminum.jpg',
    description: 'All aluminum conductor for overhead transmission and distribution lines.',
    specifications: ['1350-H19 Aluminum', 'High Conductivity', 'Lightweight', 'Reliable Overhead Service'],
    specsUrl: 'https://drive.google.com/file/d/1UdDjAxa6-YOZvAeww3P_MV-JwFA8EUnA/view?usp=sharing',
  },

  // Medium Voltage
  {
    id: 18,
    name: 'Medium Voltage Power Cable MV-90',
    category: 'medium-voltage',
    subcategory: 'mv-90',
    image: '/images/medium-voltage.jpg',
    description: 'Medium voltage power cable for use in industrial and utility power distribution.',
    specifications: ['5kV - 35kV Rated', 'EPR Insulation', 'Copper Tape Shield', 'PVC Jacket'],
    specsUrl: 'https://drive.google.com/file/d/1uD3CGR9Lw4Eo900JFo9ccWgyUJ96qzYn/view?usp=sharing',
  },
  {
    id: 19,
    name: 'Medium Voltage Power Cable MV-105',
    category: 'medium-voltage',
    subcategory: 'mv-105',
    image: '/images/medium-voltage.jpg',
    description: 'High temperature medium voltage power cable for demanding power applications.',
    specifications: ['5kV - 35kV Rated', 'EPR Insulation', '105°C Continuous Rating', 'Utility Grade'],
    specsUrl: 'https://drive.google.com/file/d/1vcJMluJr3nUN6r2KzrAYUmWEjdek13Bw/view?usp=sharing',
  },

  // Portable Cord
  {
    id: 20,
    name: 'Type SOOW UL Portable Cord',
    category: 'portable-cord',
    subcategory: 'soow-ul',
    image: '/images/portable-cord1.jpg',
    description: 'UL listed extra hard usage thermoset portable cord for industrial environments.',
    specifications: ['600V Rated', 'Oil & Water Resistant', 'MSHA Approved', 'Thermoset Rubber'],
    specsUrl: 'https://drive.google.com/file/d/1Pgc1dQfzyG2HdIqpwo8_FB1m5GGPnBs-/view?usp=sharing',
  },
  {
    id: 21,
    name: 'Type SOOW Non-UL Portable Cord',
    category: 'portable-cord',
    subcategory: 'soow-non-ul',
    image: '/images/portable-cord1.jpg',
    description: 'Non-UL extra hard usage portable cord for general industrial applications.',
    specifications: ['600V Rated', 'Highly Flexible', 'Abrasian Resistant', 'Synthetic Rubbers'],
    specsUrl: 'https://drive.google.com/file/d/1pcRwys8vBcvzBD0YbRaoRzOwX7gP6Xmq/view?usp=sharing',
  },
  {
    id: 22,
    name: 'Type SJOOW UL Portable Cord',
    category: 'portable-cord',
    subcategory: 'sjoow',
    image: '/images/portable-cord1.jpg',
    description: 'Junior hard usage thermoset portable cord for light duty industrial and commercial use.',
    specifications: ['300V Rated', 'Oil & Water Resistant', 'Highly Flexible', 'UL Listed'],
    specsUrl: 'https://drive.google.com/file/d/1kOkuAmNPzwjPknHyMsR8Q_w9ydJ6on3D/view?usp=sharing',
  },
  {
    id: 23,
    name: 'Welding Cable',
    category: 'portable-cord',
    subcategory: 'welding-cable',
    image: '/images/welding-cable.jpg',
    description: 'Extremely flexible welding cable for power supply to welding equipment.',
    specifications: ['600V Rated', 'High Flexibility', 'Tough Neoprene Jacket', 'RoHS Compliant'],
    specsUrl: 'https://drive.google.com/file/d/1LulrYXRSJrZx07Lr8UUPsKEySha8WvKn/view?usp=sharing',
  }
];

const PageContainer = styled.div`
  padding: 2rem 0 5rem;
  background-color: ${({ theme }) => theme.colors.darkest};
  color: ${({ theme }) => theme.colors.light};
`;

const PageHeader = styled.div`
  background-color: ${({ theme }) => theme.colors.dark};
  color: ${({ theme }) => theme.colors.light};
  padding: 3rem 1rem 2rem;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 2.5rem 1rem 1.5rem;
  }
`;

const PageTitle = styled.h1`
  font-size: 2.8rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2.2rem;
  }
`;

const PageDescription = styled.p`
  font-size: 1.15rem;
  max-width: 650px;
  margin: 0 auto 2rem;
  color: ${({ theme }) => theme.colors.lightGray};
  line-height: 1.6;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const ProductsSection = styled.section`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem 1rem 4rem;
  display: flex;
  flex-direction: row;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex-direction: column;
    padding-top: 0;
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  position: relative;
  
  svg {
    position: absolute;
    left: 1.25rem;
    color: ${({ theme }) => theme.colors.lightGray};
    z-index: 1;
  }
  
  input {
    width: 100%;
    padding: 1rem 1rem 1rem 3rem;
    border: 2px solid ${({ theme }) => theme.colors.darker};
    border-radius: 30px;
    font-family: ${({ theme }) => theme.fonts.primary};
    font-size: 1.1rem;
    background-color: ${({ theme }) => theme.colors.darkest};
    color: ${({ theme }) => theme.colors.light};
    transition: all 0.3s ease;
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
    
    &:focus {
      outline: none;
      border-color: ${({ theme }) => theme.colors.primary};
      box-shadow: 0 0 15px rgba(247, 141, 45, 0.4);
      background-color: ${({ theme }) => theme.colors.darker};
    }

    &::placeholder {
      color: ${({ theme }) => theme.colors.darkGray};
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CategoryFilters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 1rem 0;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const CategoryFilter = styled.button<{ isActive: boolean }>`
  padding: 0.5rem 1rem;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.darker};
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.light : theme.colors.lightGray};
  border: 1px solid ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  font-size: 0.9rem;
  font-weight: 500;
  
  &:hover {
    background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.dark};
  }
`;

const ProductCount = styled.div`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 1.5rem;
`;

const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

const ProductCard = styled.div`
  background-color: ${({ theme }) => theme.colors.darker};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.small};
  transition: ${({ theme }) => theme.transitions.medium};
  height: 100%;
  display: flex;
  flex-direction: column;
  color: ${({ theme }) => theme.colors.light};
  text-decoration: none;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
  }
`;

const ProductImageContainer = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  background-color: ${({ theme }) => theme.colors.dark};
`;

const ProductImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform ${({ theme }) => theme.transitions.medium};
  
  ${ProductCard}:hover & {
    transform: scale(1.05);
  }
`;

const ProductContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const ProductName = styled.h3`
  font-size: 1.25rem;
  margin-bottom: 0.75rem;
  color: ${({ theme }) => theme.colors.light};
`;

const ProductDescription = styled.p`
  color: ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 1rem;
  flex: 1;
`;

const ProductSpecs = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0 0 1rem;
`;

const SpecItem = styled.li`
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.lightGray};
  margin-bottom: 0.5rem;
  position: relative;
  padding-left: 1.25rem;
  
  &::before {
    content: '•';
    color: ${({ theme }) => theme.colors.primary};
    position: absolute;
    left: 0;
    font-size: 1.2rem;
  }
`;

const NoResults = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: ${({ theme }) => theme.colors.lightGray};
  
  h3 {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }
  
  p {
    margin-bottom: 1.5rem;
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: ${({ theme }) => theme.colors.primary};
  color: ${({ theme }) => theme.colors.light};
  padding: 0.75rem 1.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 600;
  transition: ${({ theme }) => theme.transitions.short};
  text-decoration: none;
  
  &:hover {
    background-color: #e07b29;
    transform: translateY(-2px);
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

// Add these new styled components for the subcategory functionality
const SubcategoryList = styled.div<{ isVisible: boolean }>`
  display: ${({ isVisible }) => (isVisible ? 'block' : 'none')};
  margin-top: 0.25rem;
  margin-bottom: 0.75rem;
  margin-left: 1rem;

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    display: none; /* Hide subcategories on mobile nav to keep it clean, user can still see products per category */
  }
`;

const SubcategoryButton = styled.button<{ isActive: boolean }>`
  width: 100%;
  text-align: left;
  padding: 0.5rem 0.75rem;
  background-color: transparent;
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.lightGray};
  border: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  font-size: 0.85rem;
  
  &:hover {
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const CategoryChevron = styled.span`
  float: right;
  margin-top: 3px;
`;

const AddToQuoteButton = styled.button<{ isInQuote: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  margin-top: auto;
  background-color: ${({ theme, isInQuote }) => isInQuote ? theme.colors.success : theme.colors.primary};
  color: ${({ theme }) => theme.colors.light};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 500;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: ${({ theme, isInQuote }) => isInQuote ? theme.colors.success : '#e07b29'};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;

const SpecsSheetButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.5rem 1rem;
  margin-top: 0.5rem;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.light};
  }
  
  svg {
    margin-right: 0.5rem;
  }
`;


// Define notification type
type NotificationType = 'success' | 'error' | 'info';

// Update the ProductsPage component
const ProductsPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [imageErrors, setImageErrors] = useState<Record<number, boolean>>({});

  const [notification, setNotification] = useState<{
    show: boolean;
    message: string;
    type: NotificationType
  }>({
    show: false,
    message: '',
    type: 'success'
  });

  const { addToQuote, removeFromQuote, isInQuote } = useQuote(); // Reverted: removed 'quote' variable

  // Memoized search handler to prevent unnecessary recreations
  const handleSearch = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  // Memoized category change handler
  const handleCategoryChange = useCallback((categoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory('');
  }, []);

  // Handle image loading error
  const handleImageError = useCallback((productId: number) => {
    setImageErrors(prev => ({ ...prev, [productId]: true }));
  }, []);


  // Memoized category expansion handler
  const toggleCategoryExpansion = useCallback((categoryId: string, event: React.MouseEvent) => {
    event.stopPropagation();

    setExpandedCategories(prev => {
      if (prev.includes(categoryId)) {
        return prev.filter(id => id !== categoryId);
      } else {
        return [...prev, categoryId];
      }
    });
  }, []);

  // Memoized subcategory change handler
  const handleSubcategoryChange = useCallback((categoryId: string, subcategoryId: string) => {
    setSelectedCategory(categoryId);
    setSelectedSubcategory(subcategoryId);
  }, []);

  // Memoized add/remove quote handler
  const handleAddToQuote = useCallback((product: any) => {
    // Check if the product is already in the quote
    if (isInQuote(product.id)) {
      // Remove from quote if already added
      removeFromQuote(product.id);

      setNotification({
        show: true,
        message: `${product.name} removed from your quote`,
        type: 'error'
      });
    } else {
      // Add to quote if not already added
      addToQuote({
        id: product.id,
        name: product.name,
        category: product.category,
        description: product.description,
        specifications: product.specifications
      });

      setNotification({
        show: true,
        message: `${product.name} added to your quote`,
        type: 'success'
      });
    }

    // Hide notification after 3 seconds
    setTimeout(() => {
      setNotification({ show: false, message: '', type: 'success' });
    }, 3000);
  }, [addToQuote, removeFromQuote, isInQuote]);

  const handleCloseNotification = useCallback(() => {
    setNotification({ show: false, message: '', type: 'success' });
  }, []);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 20;

  // Memoized filter function to prevent recalculating on every render
  const filteredProducts = useMemo(() => {
    return PRODUCTS.filter((product) => {
      const matchesSearch = searchTerm === '' ||
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (product.description || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        selectedCategory === 'all' ||
        (selectedSubcategory ?
          product.category === selectedCategory && product.subcategory === selectedSubcategory :
          product.category === selectedCategory);
      return matchesSearch && matchesCategory;
    });
  }, [searchTerm, selectedCategory, selectedSubcategory]);

  // Get current products for pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = useMemo(() => {
    return filteredProducts.slice(indexOfFirstProduct, indexOfLastProduct);
  }, [filteredProducts, indexOfFirstProduct, indexOfLastProduct]);

  // Change page
  const handlePageChange = useCallback((pageNumber: number) => {
    setCurrentPage(pageNumber);
    // Scroll to top when page changes
    window.scrollTo(0, 0);
  }, []);

  // Calculate total pages
  const totalPages = Math.ceil(filteredProducts.length / productsPerPage);

  // SEO related: Determine current category/subcategory name for dynamic titles
  const currentCategoryName = useMemo(() => {
    if (selectedCategory === 'all') return 'All Products';
    const category = CATEGORIES.find(cat => cat.id === selectedCategory);
    if (!category) return 'Products';
    if (selectedSubcategory) {
      const subcategory = category.subcategories?.find(sub => sub.id === selectedSubcategory);
      return subcategory ? `${subcategory.name} - ${category.name} ` : category.name;
    }
    return category.name;
  }, [selectedCategory, selectedSubcategory]);

  const pageTitle = `${currentCategoryName} | ElectroCable`;
  const pageDescription = `Browse our wide selection of ${currentCategoryName.toLowerCase()} including copper and aluminum wire and cable.Quality products for all electrical needs.`;
  const pageUrl = `https://www.yourwebsite.com/products`; // Replace with your actual domain and path
  const ogImageUrl = `https://www.yourwebsite.com/images/default-og-image.jpg`; // Replace with a relevant OG image URL

  return (
    <PageContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={ogImageUrl} />
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={pageTitle} />
        <meta property="twitter:description" content={pageDescription} />
        <meta property="twitter:image" content={ogImageUrl} />

        {/* JSON-LD Structured Data for Products */}
        {currentProducts.map(product => (
          <script type="application/ld+json" key={`product-schema-${product.id}`}>
            {JSON.stringify({
              "@context": "https://schema.org/",
              "@type": "Product",
              "name": product.name,
              "image": product.image ? `https://www.yourwebsite.com${product.image}` : ogImageUrl, // Use product image or fallback
              "description": product.description || product.name,
              "sku": product.id.toString(), // Assuming product ID can serve as SKU
              "mpn": product.id.toString(), // Assuming product ID can serve as MPN
              "brand": {
                "@type": "Brand",
                "name": "ElectroCable"
              },
              // Add offers if you have pricing information
              "offers": {
                "@type": "Offer",
                "priceCurrency": "USD", // Change if needed
                // "price": "0.00", // Uncomment and set if you have price
                "availability": "https://schema.org/InStock", // Or OutOfStock, PreOrder, etc.
                "url": `${pageUrl}#product-${product.id}` // Link to product on the page
              },
              // Add aggregateRating if you have reviews
              // "aggregateRating": {
              //   "@type": "AggregateRating",
              //   "ratingValue": "4.5", // Example value
              //   "reviewCount": "100"  // Example value
              // }
            })}
          </script>
        ))}
      </Helmet>

      {/* Notification component */}
      <Notification
        message={notification.message}
        type={notification.type}
        $isVisible={notification.show}
        onClose={handleCloseNotification}
      />

      <PageHeader>
        <PageTitle>Our Products</PageTitle>
        <PageDescription>
          High Quality wire and cable products for all electrical needs.
        </PageDescription>
        <FiltersContainer>
          <SearchBar>
            <IconWrapper icon={FaSearch} size={18} />
            <input
              type="text"
              placeholder="Search products..."
              value={searchTerm}
              onChange={handleSearch}
            />
          </SearchBar>
        </FiltersContainer>
      </PageHeader>

      <ProductsSection>
        <CategorySidebar>
          <MainCategoryItem isActive={selectedCategory === 'all'}>
            <MainCategoryButton
              isActive={selectedCategory === 'all'}
              onClick={() => {
                setSelectedCategory('all');
                setSelectedSubcategory('');
              }}
              className="all-products-btn"
            >
              All Products
            </MainCategoryButton>
          </MainCategoryItem>

          {CATEGORIES.filter(category => category.id !== 'all').map((category) => (
            <MainCategoryItem key={category.id} isActive={selectedCategory === category.id}>
              <MainCategoryButton
                isActive={selectedCategory === category.id && !selectedSubcategory}
                onClick={() => handleCategoryChange(category.id)}
              >
                {category.name}
                {category.subcategories && category.subcategories.length > 0 && (
                  <CategoryChevron onClick={(e) => toggleCategoryExpansion(category.id, e)}>
                    {expandedCategories.includes(category.id) ?
                      <IconWrapper icon={FaChevronDown} size={12} /> :
                      <IconWrapper icon={FaChevronRight} size={12} />}
                  </CategoryChevron>
                )}
              </MainCategoryButton>

              {category.subcategories && category.subcategories.length > 0 && (
                <SubcategoryList isVisible={expandedCategories.includes(category.id)}>
                  {category.subcategories.map((subcategory) => (
                    <SubcategoryButton
                      key={subcategory.id}
                      isActive={selectedCategory === category.id && selectedSubcategory === subcategory.id}
                      onClick={() => handleSubcategoryChange(category.id, subcategory.id)}
                    >
                      {subcategory.name}
                    </SubcategoryButton>
                  ))}
                </SubcategoryList>
              )}
            </MainCategoryItem>
          ))}
        </CategorySidebar>

        <ProductsContent>
          <ProductCount>
            Showing {filteredProducts.length} {filteredProducts.length === 1 ? 'product' : 'products'}
            {filteredProducts.length > productsPerPage && ` (Page ${currentPage} of ${totalPages})`}
          </ProductCount>

          {filteredProducts.length > 0 ? (
            <>
              <ProductGrid>
                {currentProducts.map((product) => (
                  <ProductCard key={product.id}>
                    {product.image && !imageErrors[product.id] && (
                      <ProductImageContainer>
                        <ProductImage
                          src={product.image}
                          alt={product.name}
                          onError={() => handleImageError(product.id)}
                        />
                      </ProductImageContainer>
                    )}
                    <ProductContent>
                      <ProductName>{product.name}</ProductName>
                      <ProductDescription>{product.description}</ProductDescription>
                      <ProductSpecs>
                        {product.specifications.map((spec, index) => (
                          <SpecItem key={index}>{spec}</SpecItem>
                        ))}
                      </ProductSpecs>
                      <AddToQuoteButton
                        isInQuote={isInQuote(product.id)}
                        onClick={() => handleAddToQuote(product)}
                      >
                        {isInQuote(product.id) ? (
                          <>
                            <IconWrapper icon={FaCheck} size={14} />
                            Added to Quote
                          </>
                        ) : (
                          <>
                            <IconWrapper icon={FaPlus} size={14} />
                            Add to Quote
                          </>
                        )}
                      </AddToQuoteButton>
                      {product.specsUrl && product.specsUrl !== 'LINK' && (
                        <SpecsSheetButton href={product.specsUrl} target="_blank" rel="noopener noreferrer">
                          <IconWrapper icon={FaExternalLinkAlt} size={14} />
                          Specs Sheet
                        </SpecsSheetButton>
                      )}
                    </ProductContent>
                  </ProductCard>
                ))}
              </ProductGrid>

              {/* Pagination controls */}
              {totalPages > 1 && (
                <Pagination>
                  <PaginationButton
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </PaginationButton>

                  <PageNumbers>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(num =>
                        num === 1 ||
                        num === totalPages ||
                        (num >= currentPage - 1 && num <= currentPage + 1)
                      )
                      .map((number, index, array) => (
                        <React.Fragment key={number}>
                          {index > 0 && array[index - 1] !== number - 1 && <PageEllipsis>...</PageEllipsis>}
                          <PageNumber
                            isActive={currentPage === number}
                            onClick={() => handlePageChange(number)}
                          >
                            {number}
                          </PageNumber>
                        </React.Fragment>
                      ))}
                  </PageNumbers>

                  <PaginationButton
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </PaginationButton>
                </Pagination>
              )}
            </>
          ) : (
            <NoResults>
              <h3>No products found</h3>
              <p>
                Can't find what you need?  We offer custom solutions for specific requirements. <br />
                Our team can help you find or develop the perfect cable for your project.
              </p>
              <ContactButton to="/contact">
                <IconWrapper icon={FaEnvelope} size={16} />
                Contact Us for Custom Solutions
              </ContactButton>
            </NoResults>
          )}
        </ProductsContent>
      </ProductsSection>
    </PageContainer>
  );
};

// Add these new styled components for the sidebar layout
const CategorySidebar = styled.div`
  width: 280px;
  padding: 1rem;
  background-color: ${({ theme }) => theme.colors.darker};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-right: 2rem;
  max-height: 800px;
  overflow-y: auto;
  
  /* Customize scrollbar for better appearance */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.colors.darkest};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.colors.lightGray};
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.colors.primary};
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: 100vw;
    margin-right: -1rem;
    margin-left: -1rem;
    padding: 0.75rem 1rem;
    margin-bottom: 1.5rem;
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    position: sticky;
    top: 98px; /* Properly offset to account for TopBar + MainHeader */
    z-index: 100;
    background-color: ${({ theme }) => theme.colors.darkest};
    border-radius: 0;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.3);
    -webkit-overflow-scrolling: touch;
    
    &::-webkit-scrollbar {
      display: none;
    }
    scrollbar-width: none;
  }
`;

const MainCategoryItem = styled.div<{ isActive: boolean }>`
  @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    flex: 0 0 auto;
    margin-right: 1.5rem;
    
    &:last-child {
      padding-right: 2rem;
    }
  }
`;

const MainCategoryButton = styled.button<{ isActive: boolean }>`
  width: 100%;
  text-align: left;
  padding: 0.75rem 1rem;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.darker};
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.light : theme.colors.lightGray};
  border: 1px solid ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  font-size: 0.95rem;
  font-weight: 500;
  
  &:hover {
    background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.dark};
  }

  &.all-products-btn {
    @media (min-width: ${({ theme }) => theme.breakpoints.lg}) {
      margin-bottom: 1rem;
    }
  }

  @media (max-width: ${({ theme }) => theme.breakpoints.lg}) {
    width: auto;
    padding: 0.5rem 1rem;
    font-size: 0.85rem;
    border-radius: 20px; /* Chip style */
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const ProductsContent = styled.div`
  flex: 1;
`;

// Pagination components
const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 2rem;
  margin-bottom: 1rem;
`;

const PaginationButton = styled.button<{ disabled: boolean }>`
  padding: 0.5rem 1rem;
  background-color: ${({ theme, disabled }) =>
    disabled ? theme.colors.lightGray : theme.colors.primary};
  color: ${({ theme }) => theme.colors.light};
  border: none;
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
  opacity: ${({ disabled }) => disabled ? 0.6 : 1};
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover:not(:disabled) {
    background-color: #e07b29;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  align-items: center;
  margin: 0 1rem;
`;

const PageNumber = styled.button<{ isActive: boolean }>`
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 0.25rem;
  background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : 'transparent'};
  color: ${({ theme, isActive }) =>
    isActive ? theme.colors.light : theme.colors.lightGray};
  border: 1px solid ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.lightGray};
  border-radius: ${({ theme }) => theme.borderRadius.small};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.short};
  
  &:hover {
    background-color: ${({ theme, isActive }) =>
    isActive ? theme.colors.primary : theme.colors.darker};
  }
`;

const PageEllipsis = styled.span`
  margin: 0 0.25rem;
  color: ${({ theme }) => theme.colors.lightGray};
`;

export default ProductsPage; 